import { FunctionComponent, useState } from 'react';
import { Box, Typography } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import clsx from 'clsx';
import Marketplace, { SETTINGS_TRUE_VALUE } from '@spike/marketplace-model';
import { Button } from 'components/UI';
import { useApiClientWrapper, useMarketplace, useMasterData } from 'hooks';
import { useNonInitialEffect } from '@versiondos/hooks';
import {
    MarketplaceStatus,
    SERVICE_CATEGORY_SETTINGS,
    ServiceCategoryId,
    saveServiceCategoriesThunk
} from '@spike/marketplace-action';
import { BusinessSettingsTitle } from '../UI';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import Checkbox from 'components/UI/V3/Forms/Checkbox';
import styles from './AdditionalInfo.module.scss';
import MasterData from '@spike/masterdata-model';

// Pick-Up / Drop-Off has no effect anywhere in the product, so it is not offered here.
// The stored pickup_enabled setting is left untouched.
const HIDDEN_CATEGORIES: Array<ServiceCategoryId> = ['pick_up'];

// House calls are a location option for grooming services, not a category of their own:
// there is no mobile_grooming business area, the flag only enables the "house call"
// option inside grooming services. Enabling it without grooming leaves the app in a
// state where the bookings tab shows but the calendar and services nav do not.
const GROOMING_CATEGORY: ServiceCategoryId = 'grooming';
const HOUSE_CALL_CATEGORY: ServiceCategoryId = 'mobile_grooming';

export interface Props {
    className?: string;
    openSidebar?: () => void;
    isMobile?: boolean;
}

export interface ServiceCategory {
    id: ServiceCategoryId;
    name: string;
    selected: boolean;
}

// Name and id come from master data, not from the setting row: a marketplace that was
// never seeded a given key would otherwise render a blank, id-less checkbox.
// Categories master data may add that we have no setting mapped for are skipped.
export const getServiceCategories = (marketplace: Marketplace, masterData: MasterData): Array<ServiceCategory> => {
    const categories = masterData.serviceCategories.reduce<Array<ServiceCategory>>((result, category) => {
        const id = category.id as ServiceCategoryId;
        const setting = SERVICE_CATEGORY_SETTINGS[id];

        if (setting && !HIDDEN_CATEGORIES.includes(id)) {
            result.push({
                id,
                name: setting.name,
                selected: marketplace.settings?.find(s => s.key === setting.key)?.value === SETTINGS_TRUE_VALUE
            });
        }

        return result;
    }, []);

    // Marketplaces configured before house calls depended on grooming can arrive with
    // grooming off and house calls on. Show that as off so it is not stuck checked and
    // greyed out, and so the next save persists the correction.
    const groomingSelected = categories.find(category => category.id === GROOMING_CATEGORY)?.selected ?? false;

    return groomingSelected
        ? categories
        : categories.map(category =>
              category.id === HOUSE_CALL_CATEGORY ? { ...category, selected: false } : category
          );
};

export const AdditionalInfo: FunctionComponent<Props> = props => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const masterData = useMasterData();
    const marketplace = useMarketplace();

    const status = useSelector<RootState, MarketplaceStatus>(state => state.marketplace.status);
    const [saved, setSaved] = useState(false);
    const [loading, setLoading] = useState(false);
    const [categories, setCategories] = useState<Array<ServiceCategory>>(getServiceCategories(marketplace, masterData));

    const noneSelected = categories.every(category => !category.selected);
    const groomingSelected = categories.find(category => category.id === GROOMING_CATEGORY)?.selected ?? false;

    useNonInitialEffect(() => {
        if (status === MarketplaceStatus.SaveServiceCategoriesSuccess) {
            setSaved(true);
            setLoading(false);
        }
        if (status === MarketplaceStatus.Error) {
            setLoading(false);
        }
    }, [status]);

    const changeServiceCategoriesHandler = (checked: boolean, categoryId: ServiceCategoryId) => {
        // Turning grooming off drags house calls with it: they cannot be offered on their own.
        const clearsHouseCall = categoryId === GROOMING_CATEGORY && !checked;

        setSaved(false);
        setCategories(prevCategories =>
            prevCategories.map(category => {
                if (category.id === categoryId) return { ...category, selected: checked };
                if (clearsHouseCall && category.id === HOUSE_CALL_CATEGORY) {
                    return { ...category, selected: false };
                }
                return category;
            })
        );
    };

    const saveHandler = () => {
        setLoading(true);
        dispatch(
            saveServiceCategoriesThunk(
                apiClientWrapper,
                Object.fromEntries(categories.map(category => [category.id, category.selected]))
            )
        );
    };

    return (
        <Box className={clsx(styles.container, props.className)}>
            <Box className={styles.titleContainer}>
                <BusinessSettingsTitle
                    className={styles.businessSettingsTittle}
                    containerClassName={styles.businessSettingsTittleContainer}
                >
                    {' '}
                    {props.isMobile && (
                        <FontAwesomeIcon
                            className={styles.iconBusinessTitle}
                            onClick={props.openSidebar}
                            icon={faArrowLeft}
                            size="2x"
                        />
                    )}{' '}
                    Service Categories
                </BusinessSettingsTitle>
            </Box>

            <Box className={clsx(styles.row, styles.column)}>
                <Box>
                    <Typography className={styles.sublabel}>Choose the types of services you offer: </Typography>
                </Box>
                <Box className={styles.radioButtonContainer}>
                    <div className={styles.checkboxContainer}>
                        {categories.map(service => {
                            const isHouseCall = service.id === HOUSE_CALL_CATEGORY;

                            return (
                                <div
                                    key={service.id}
                                    className={clsx({
                                        [styles.nested]: isHouseCall,
                                        [styles.disabled]: isHouseCall && !groomingSelected
                                    })}
                                >
                                    <Checkbox
                                        key={service.id}
                                        label={service.name}
                                        checked={service.selected}
                                        onChange={checked => changeServiceCategoriesHandler(checked, service.id)}
                                    />
                                </div>
                            );
                        })}
                    </div>
                </Box>
            </Box>

            <Box className={clsx(styles.buttonContainer, styles.buttonContainerFull)}>
                <Button
                    id="business_settings_additional_info_button_save"
                    label={saved ? 'Saved!' : 'Save'}
                    onClick={saveHandler}
                    loading={loading}
                    disabled={noneSelected}
                />
            </Box>
        </Box>
    );
};

export default AdditionalInfo;
